/**
 * Members list route (Organizers only)
 * Browse family members with their constraints
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useUsers } from '@/hooks/useUsers';
import { useConstraints } from '@/hooks/useConstraints';
import { requireOrganizer } from '@/lib/auth/routeGuards';
import { Loading } from '@/components/ui/Loading';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import { EmptyState } from '@/components/ui/EmptyState';
import type { Constraint, User } from '@/types/api';

export const Route = createFileRoute('/users/')({
  beforeLoad: requireOrganizer,
  component: UsersListComponent,
});

// Generate a consistent color from a string (user name)
function getAvatarColor(name: string): string {
  const colors = [
    '#5B4FE9', // Purple
    '#10B981', // Green
    '#F59E0B', // Amber
    '#EF4444', // Red
    '#3B82F6', // Blue
    '#8B5CF6', // Violet
    '#EC4899', // Pink
    '#14B8A6', // Teal
  ];

  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }

  return colors[Math.abs(hash) % colors.length];
}

// Get initials from name
function getInitials(name: string): string {
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

// Get constraint type styling
function getConstraintStyle(type: string): { bg: string; text: string } {
  switch (type) {
    case 'allergy':
      return { bg: 'bg-red-100', text: 'text-red-800' };
    case 'dislike':
      return { bg: 'bg-yellow-100', text: 'text-yellow-800' };
    case 'diet':
      return { bg: 'bg-green-100', text: 'text-green-800' };
    case 'medical':
      return { bg: 'bg-purple-100', text: 'text-purple-800' };
    default:
      return { bg: 'bg-gray-100', text: 'text-gray-800' };
  }
}

interface MemberCardProps {
  user: User;
  constraints: Constraint[];
  onClick: () => void;
}

function MemberCard({ user, constraints, onClick }: MemberCardProps) {
  const { t } = useTranslation();
  const avatarColor = getAvatarColor(user.name);

  return (
    <div
      className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow cursor-pointer"
      onClick={onClick}
    >
      {/* Header with avatar and name */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm"
            style={{ backgroundColor: avatarColor }}
          >
            {getInitials(user.name)}
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{user.name}</h3>
          </div>
        </div>
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            user.isOrganizer
              ? 'bg-purple-100 text-purple-800'
              : 'bg-gray-100 text-gray-600'
          }`}
        >
          {user.isOrganizer ? t('users.organizer') : t('users.member')}
        </span>
      </div>

      {/* Constraints */}
      {constraints.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {constraints.slice(0, 3).map((constraint) => {
            const style = getConstraintStyle(constraint.constraintType);
            return (
              <span
                key={constraint.id}
                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${style.bg} ${style.text}`}
              >
                {t(`constraint.${constraint.constraintType}`)}
              </span>
            );
          })}
          {constraints.length > 3 && (
            <span className="text-xs text-gray-500">
              +{constraints.length - 3}
            </span>
          )}
        </div>
      )}

      {/* Default availability */}
      <div className="flex items-center gap-1.5 text-sm text-gray-500 mb-3">
        {user.defaultAvailability ? (
          <>
            <svg
              className="h-4 w-4 text-green-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <span>{t('users.defaultAvailabilityShort')}</span>
          </>
        ) : (
          <>
            <svg
              className="h-4 w-4 text-gray-400"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            <span>{t('users.notDefaultAvailability')}</span>
          </>
        )}
      </div>

      {/* View profile link */}
      <span className="text-sm font-medium text-indigo-600 hover:text-indigo-800">
        {t('users.viewProfile')} →
      </span>
    </div>
  );
}

function UsersListComponent() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data, isLoading, error } = useUsers();
  const { data: constraints = [], isLoading: constraintsLoading } = useConstraints();

  // Group constraints by userId
  const constraintsByUser = constraints.reduce<Record<number, Constraint[]>>(
    (acc, constraint) => {
      if (!acc[constraint.userId]) {
        acc[constraint.userId] = [];
      }
      acc[constraint.userId].push(constraint);
      return acc;
    },
    {}
  );

  if (isLoading || constraintsLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {t('users.title')}
          </h1>
          <Loading />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {t('users.title')}
          </h1>
          <ErrorMessage
            title={t('users.errorLoading')}
            message={error instanceof Error ? error.message : t('errors.generic')}
          />
        </div>
      </div>
    );
  }

  const users = data?.users || [];

  if (users.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {t('users.title')}
          </h1>
          <EmptyState
            title={t('users.noUsers')}
            description={t('users.noUsersDescription')}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">
            {t('users.title')}
          </h1>
          <p className="mt-1 text-sm text-gray-600">{t('users.description')}</p>
        </div>

        {/* Members count */}
        <h2 className="text-base font-semibold text-gray-900 mb-4">
          {t('users.membersCount', { count: users.length })}
        </h2>

        {/* Members Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {users.map((user) => (
            <MemberCard
              key={user.id}
              user={user}
              constraints={constraintsByUser[user.id] || []}
              onClick={() => navigate({ to: `/users/${user.id}` })}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
